/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-charcoal-900 mb-10 transition-colors" data-testid="back-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="max-w-sm w-full">
          <div className="w-10 h-10 rounded-xl bg-charcoal-900 grid place-items-center mb-6">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-charcoal-900">Forgot your password?</h1>
          <p className="text-charcoal-500 mt-2 text-sm">Enter the email you used to sign up and we'll send you a reset link. It expires in 1 hour.</p>

          {sent ? (
            <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-100 p-5" data-testid="reset-sent-state">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-900">Check your inbox</p>
                  <p className="text-sm text-emerald-800 mt-1 leading-relaxed">
                    If an account exists for <span className="font-mono">{email}</span>, we just sent a password reset link. Check your inbox (and spam folder). The link expires in 1 hour.
                  </p>
                </div>
              </div>
              <button onClick={() => setSent(false)}
                className="mt-4 text-sm text-emerald-800 font-medium hover:underline">
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <label className="text-xs font-medium text-charcoal-700">Email</label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-300" />
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-11 pl-10 pr-3.5 rounded-xl border border-black/10 bg-white text-charcoal-900 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                    placeholder="you@nonprofit.org" data-testid="forgot-email" />
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full h-11 rounded-xl bg-brand text-white font-medium text-sm hover:bg-brand-hover transition-colors disabled:opacity-60"
                data-testid="forgot-submit">
                {loading ? "Sending link…" : "Send reset link"}
              </button>
            </form>
          )}
          <p className="text-sm text-charcoal-500 mt-6">
            Remembered it? <Link to="/login" className="text-brand font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative bg-charcoal-900 tech-grid overflow-hidden">
        <div className="absolute inset-0 flex flex-col justify-center px-16">
          <p className="text-emerald-400 font-mono text-xs tracking-[0.2em] uppercase mb-4">Account recovery</p>
          <p className="text-white text-3xl font-medium leading-snug tracking-tight max-w-md">
            "We'll get you back to watching over your mission in under a minute."
          </p>
        </div>
      </div>
    </div>
  );
}
